interface String {
    /**
     * Compares two string and returns an indication of their relative sort order, taking into consideration any numeric characters such that the
     * items are sorted in a more user-friendly manner. For example, "Item9" will come before "Item10", while a standard string comparison would do
     * the opposite.
     * @param that The string to compare to the target string.
     * @param ignoreCase A value indicating whether to perform a case-sensitive or case-insensitive string comparison on non-numeric characters.
     */
    localeCompareAdvanced(that: string, ignoreCase?: boolean): number
    /**
     * Returns a new string in which a specified string is inserted at a specified index position in this instance.
     * @param index The index at which to insert the new string value.
     * @param value The string to insert.
     */
    insert(index: number, value: string): string
    /**
     * Returns a new string in which a specified number of characters in the current instance beginning at a specified position have been deleted.
     * @param index The index at which to begin deleting characters.
     * @param length The number of characters to delete. If omitted or less than or equal to zero, all remaining characters are deleted.
     */
    remove(index: number, length?: number): string
    /**
     * Replaces all occurrences of text in a string, using a search string.
     * @param searchValue A String object or string literal that represents the text to replace.
     * @param replaceValue A String object or string literal containing the text to replace for every successful match.
     * @param ignoreCase An optional value indicating whether to ignore case when attempting to match. The default is 'false'.
     */
    //replaceAll(searchValue: string, replaceValue: string, ignoreCase?: boolean): string;
    /**
     * Replaces the format item in the string with the string representation of a corresponding object in a specified object list.
     * @param args An array that contains zero or more items to format.
     */
    format(...args: Object[]): string
    /**
     * Outputs the string to the console, if available.
     */
    log(): void
}
String.prototype.localeCompareAdvanced = function (that: string, ignoreCase = true): number {
    var a = this.toString() // the literal string value, not the string object
    var b = that

    // Handle non-values...
    if (!a || !b) {
        if (a == b) return 0
        if (a > b) return 1
        if (a < b) return -1
    }

    // Ignore case...
    if (ignoreCase) {
        a = a.toLocaleLowerCase()
        b = b.toLocaleLowerCase()
    }

    // Handle numbers in text...
    var offset = 48
    var index = 0
    while (true) {
        // Digit testing function...
        function isDigit(value: number) {
            return value >= 0 && value <= 9
        }

        // Look for numbers...
        while (index < a.length && index < b.length) {
            var increment = 1
            var digit1 = a.charCodeAt(index) - offset
            var digit2 = b.charCodeAt(index) - offset
            if (isDigit(digit1) && isDigit(digit2)) {
                // Get number from first text...
                var number1 = digit1
                while (index + increment < a.length) {
                    var digit = a.charCodeAt(index + increment) - offset
                    if (isDigit(digit)) {
                        number1 = number1 * 10 + digit
                        ++increment
                    } else break
                }

                // Get number from second text...
                increment = 1
                var number2 = digit2
                while (index + increment < b.length) {
                    var digit = b.charCodeAt(index + increment) - offset
                    if (isDigit(digit)) {
                        number2 = number2 * 10 + digit
                        ++increment
                    } else break
                }

                // Return numeric comparison result...
                if (number1 > number2) return 1
                if (number1 < number2) return -1
            } else {
                // Return character comparison result...
                var char1 = a.charAt(index)
                var char2 = b.charAt(index)

                if (char1 > char2) return 1
                if (char1 < char2) return -1
            }

            // Numbers/characters are equal - continue on...
            index += increment

            // Values are equal up to the end of the shorter one, which comes first...
            if (index >= a.length || index >= b.length) {
                return a.length - b.length
            }
        }
    }
}
String.prototype.insert = function (index: number, value: string): string {
    return index <= 0 ? value + this : this.substring(0, index) + value + this.substring(index, this.length)
}
String.prototype.remove = function (index: number, length?: number): string {
    var s = length && length > 0 ? this.substr(index, length) : this.substr(index)
    return this.replace(s, '')
}
//String.prototype.replaceAll = function (searchValue: string, replaceValue: string, ignoreCase = false): string {
//    var flags = 'g'; // global (match all occurrences)
//    if (ignoreCase) {
//        flags += 'i';
//    }
//    return this.replace(new RegExp(searchValue, flags), replaceValue);
//}
String.prototype.format = function (): string {
    var formatted = this
    for (var i = 0; i < arguments.length; i++) {
        var arg = arguments[i]
        if (arg == undefined || arg == null) {
            arg = ''
        }
        formatted = formatted.replace(RegExp('\\{' + i + '\\}', 'g'), arg.toString())
    }
    return formatted.toString()
}
String.prototype.log = function (): void {
    if (console) {
        console.log(this.format.apply(this, arguments as any).toString())
    }
}

interface Number {
    /**
     * Tests that a number is equal to another number, within a specified tolerance.
     * @param value The other number to compare the current number to.
     * @param tolerance The tolerance to use in the comparison.
     */
    isEqualTo(value: number, tolerance: number): boolean
    /**
     * Tests that a number is less than another number, using a specified tolerance.
     * @param value The other number to compare the current number to.
     * @param tolerance The tolerance to use in the comparison.
     */
    isLessThan(value: number, tolerance: number): boolean
    /**
     * Tests that a number is less than or equal to another number, using a specified tolerance.
     * @param value The other number to compare the current number to.
     * @param tolerance The tolerance to use in the comparison.
     */
    isLessThanOrEqualTo(value: number, tolerance: number): boolean
    /**
     * Tests that a number is greater than another number, using a specified tolerance.
     * @param value The other number to compare the current number to.
     * @param tolerance The tolerance to use in the comparison.
     */
    isGreaterThan(value: number, tolerance: number): boolean
    /**
     * Tests that a number is greater than or equal to another number, using a specified tolerance.
     * @param value The other number to compare the current number to.
     * @param tolerance The tolerance to use in the comparison.
     */
    isGreaterThanOrEqualTo(value: number, tolerance: number): boolean
    /**
     * Rounds the number to the specified number of places.
     *
     * @param digits The number of fractional digits in the return value.
     */
    round(digits: number): number
}
Number.prototype.isEqualTo = function (value: number, tolerance: number): boolean {
    // Handle zeros...
    if (this == 0) {
        if (value == 0) return true // both values are zero
        return value > 0 ? value < tolerance : -value < tolerance // the best we can do when only one of the values is zero
    } else if (value == 0) {
        return this.valueOf() > 0 ? this.valueOf() < tolerance : -this < tolerance // the best we can do when only one of the values is zero
    }

    // Use ratio of non-zero values...
    var delta = 1 - (this as number) / value
    return delta > 0 ? delta < tolerance : -delta < tolerance
}
Number.prototype.isLessThan = function (value: number, tolerance: number): boolean {
    return !this.isEqualTo(value, tolerance) && this.valueOf() < value
}
Number.prototype.isLessThanOrEqualTo = function (value: number, tolerance: number): boolean {
    return this.valueOf() <= value || this.isEqualTo(value, tolerance)
}
Number.prototype.isGreaterThan = function (value: number, tolerance: number): boolean {
    return !this.isEqualTo(value, tolerance) && this.valueOf() > value
}
Number.prototype.isGreaterThanOrEqualTo = function (value: number, tolerance: number): boolean {
    return this.valueOf() >= value || this.isEqualTo(value, tolerance)
}
Number.prototype.round = function (digits: number): number {
    var newNumber = new Number(this + '').toFixed(digits)
    return parseFloat(newNumber)
}

/** Specifies the day of the week. */
enum DayOfWeek {
    /** Indicates Sunday. */
    Sunday = 0,
    /** Indicates Monday. */
    Monday = 1,
    /** Indicates Tuesday. */
    Tuesday = 2,
    /** Indicates Wednesday. */
    Wednesday = 3,
    /** Indicates Thursday. */
    Thursday = 4,
    /** Indicates Friday. */
    Friday = 5,
    /** Indicates Saturday. */
    Saturday = 6,
}
interface Date {
    /**
     * Returns a new Date object with only the date component of this instance (i.e., at midnight).
     */
    date(): Date
    /**
     * Returns a new Date object that adds the specified number of minutes to the value of this instance.
     * @param value The number of minutes to add (positive or negative).
     */
    addMinutes(value: number): Date
    /**
     * Returns a new Date object that adds the specified number of hours to the value of this instance.
     * @param value The number of hours to add (positive or negative).
     */
    addHours(value: number): Date
    /**
     * Returns a new Date object that adds the specified number of days to the value of this instance.
     * @param value The number of days to add (positive or negative).
     */
    addDays(value: number): Date
    /**
     * Returns a number that represents the year and week number for the date.
     */
    yearAndWeek(): number
}
Date.prototype.date = function (): Date {
    var d = <Date>this
    return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}
Date.prototype.addMinutes = function (value: number): Date {
    var d1 = <Date>this
    var d2 = new Date(this)
    d2.setMinutes(d1.getMinutes() + value)
    return d2
}
Date.prototype.addHours = function (value: number): Date {
    var d1 = <Date>this
    var d2 = new Date(this)
    d2.setHours(d1.getHours() + value)
    return d2
}
Date.prototype.addDays = function (value: number): Date {
    var d1 = <Date>this
    var d2 = new Date(this)
    d2.setDate(d1.getDate() + value)
    return d2
}
Date.prototype.yearAndWeek = function (): number {
    const now = this
    const onejan = new Date(now.getFullYear(), 0, 1)
    const week = Math.ceil(((now.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7)
    return parseInt(now.getFullYear().toString() + week.toString())
}
interface Array<T> {
    /**
     * Creates and returns a shallow copy of the array.
     */
    copy(): T[]
    /**
     * Inserts a new item at the specified index in the array, returning a value indicating whether the item was successfully inserted.
     * @param index The index at which to insert the new item in the array.
     * @param item The item to insert into the array.
     */
    insertItem(index: number, item: T): boolean
    /**
     * Removes the first instance of the specified item from the array, returning a value indicating whether the item was successfully found and
     * removed.
     * @param item The item to remove from the array.
     */
    remove(item: T): boolean
    /**
     * Removes the item at the specified index from the array, returning a value indicating whether the item was successfully removed.
     * @param index The index at which to remove the item from the array.
     */
    removeAtIndex(index: number): boolean
    /**
     * Returns a value indicating whether the item is currently found in the array.
     * @param item The item to test.
     */
    contains(item: T): boolean
    /**
     * Creates and returns a new array containing only unique instances of items found in the source array.
     */
    distinct(): T[]
    /**
     * Combines the arrays selected from each item into a single array.
     * @param selector A function to extract an array from an item in the array.
     */
    mapMany<TValue>(selector: { (item: T): TValue[] }): TValue[]
    /**
     * Creates and returns a new array with the items sorted in ascending order according to one or more keys.
     * @param keySelectors One or more functions to extract keys from an item in the array.
     */
    orderBy(...keySelectors: { (item: T): any }[]): T[]
    /**
     * Creates and returns a new array with the items sorted in ascending order according to one or more keys. String-typed keys are compared by
     * taking into consideration any numeric characters such that the items are sorted in a more user-friendly manner. For example, "Item9" will
     * come before "Item10", while a standard ascending string comparison would do the opposite.
     * @param keySelectors One or more functions to extract keys from an item in the array.
     */
    advancedOrderBy(...keySelectors: { (item: T): any }[]): T[]
    /**
     * Creates and returns a new array with the items sorted in descending order according to one or more keys.
     * @param keySelectors One or more functions to extract keys from an item in the array.
     */
    orderByDescending(...keySelectors: { (item: T): any }[]): T[]
    /**
     * Creates and returns a new array with the items sorted in descending order according to one or more keys. String-typed keys are compared by
     * taking into consideration any numeric characters such that the items are sorted in a more user-friendly manner. For example, "Item9" will
     * come after "Item10", while a standard descending string comparison would do the opposite.
     * @param keySelectors One or more functions to extract keys from an item in the array.
     */
    advancedOrderByDescending(...keySelectors: { (item: T): any }[]): T[]
    /**
     * Moves an item from its current index to a new index, returning the updated array.
     * @param oldIndex The current index of the item to move.
     * @param newIndex The index to move the item to.
     */
    move(oldIndex: number, newIndex: number): T[]
    /**
     * Searches the sorted array for an item and returns the zero-based index of the item in the array.
     * @param item The item to search for in the sorted array.
     * @param compare A function to compare items in the sorted array.
     */
    binarySearch(item: T, compare: { (a: T, b: T): number }): number
    /**
     * Maps the array to an object with a property value for each item of the array.
     * @param propertySelector A function to extract the new object's property name from an item in the array.
     * @param valueSelector A function to extract the new object's property value from an item in the array.
     */
    toObject<TValue>(propertySelector: { (item: T): string | number }, valueSelector: { (item: T): TValue }): { [property: string]: TValue }
    /**
     * Maps the array to an object containing arrays of values from the items with the same property value.
     * @param propertySelector A function to extract the new object's property name from an item in the array.
     * @param valueSelector A function to extract the new object's property value from an item in the array.
     */
    toObjectGroups<TValue>(
        propertySelector: { (item: T): string | number },
        valueSelector: { (item: T): TValue },
    ): { [property: string]: TValue[] }
    /**
     * Returns a specified number of contiguous elements from the start of a sequence.
     *
     * @param count The number of elements to return.
     */
    take(count: number): T[]
    /**
     * Removes all items from the array
     */
    clearArray(): T[]
}
Array.prototype.copy = function (): any[] {
    return this.slice(0)
}
Array.prototype.insertItem = function (index: number, item: any): boolean {
    if (index >= 0 && index <= this.length) {
        this.splice(index, 0, item)
        return true
    }
    return false
}
Array.prototype.remove = function (item: any): boolean {
    for (var index = 0, count = this.length; index < count; ++index) {
        if (this[index] === item) {
            this.splice(index, 1)
            return true
        }
    }
    return false
}
Array.prototype.removeAtIndex = function (index: number): boolean {
    if (index >= 0 && index < this.length) {
        this.splice(index, 1)
        return true
    }
    return false
}
Array.prototype.contains = function (item: any): boolean {
    return this.indexOf(item) >= 0
}
Array.prototype.distinct = function (): any[] {
    var u = {} as any,
        a: any[] = []
    for (var i = 0, l = this.length; i < l; ++i) {
        if (u.hasOwnProperty(this[i])) {
            continue
        }
        a.push(this[i])
        u[this[i]] = 1
    }
    return a
}
Array.prototype.mapMany = function (selector: { (item: any): never[] }): any[] {
    return [].concat(...this.map(selector))
}
Array.prototype.orderBy = function (...keySelectors: { (item: any): any }[]): any[] {
    var a: any[] = this.copy()
    return a.sort((item1, item2) => {
        for (var i = 0; i < keySelectors.length; ++i) {
            var key1 = keySelectors[i](item1)
            var key2 = keySelectors[i](item2)
            if (key1 > key2) return 1
            if (key1 < key2) return -1
        }
        return 0
    })
}
Array.prototype.advancedOrderBy = function (...keySelectors: { (item: any): any }[]): any[] {
    var a: any[] = this.copy()
    return a.sort((item1, item2) => {
        for (var i = 0; i < keySelectors.length; ++i) {
            var key1 = keySelectors[i](item1)
            var key2 = keySelectors[i](item2)
            if (typeof key1 == 'string' && typeof key2 == 'string') {
                var result = key1.localeCompareAdvanced(key2)
                if (result != 0) return result
            } else {
                if (key1 > key2) return 1
                if (key1 < key2) return -1
            }
        }
        return 0
    })
}
Array.prototype.orderByDescending = function (...keySelectors: { (item: any): any }[]): any[] {
    var a: any[] = this.copy()
    return a.sort((item1, item2) => {
        for (var i = 0; i < keySelectors.length; ++i) {
            var key1 = keySelectors[i](item1)
            var key2 = keySelectors[i](item2)
            if (key1 > key2) return -1
            if (key1 < key2) return 1
        }
        return 0
    })
}
Array.prototype.advancedOrderByDescending = function (...keySelectors: { (item: any): any }[]): any[] {
    var a: any[] = this.copy()
    return a.sort((item1, item2) => {
        for (var i = 0; i < keySelectors.length; ++i) {
            var key1 = keySelectors[i](item1)
            var key2 = keySelectors[i](item2)
            if (typeof key1 == 'string' && typeof key2 == 'string') {
                var result = key2.localeCompareAdvanced(key1)
                if (result != 0) return result
            } else {
                if (key1 > key2) return -1
                if (key1 < key2) return 1
            }
        }
        return 0
    })
}
Array.prototype.move = function (oldIndex: number, newIndex: number): any[] {
    while (oldIndex < 0) {
        oldIndex += this.length
    }
    while (newIndex < 0) {
        newIndex += this.length
    }
    if (newIndex >= this.length) {
        var k = newIndex - this.length
        while (k-- + 1) {
            this.push(undefined)
        }
    }
    this.splice(newIndex, 0, this.splice(oldIndex, 1)[0])
    return this
}
Array.prototype.binarySearch = function (item: any, compare: { (a: any, b: any): number }): number {
    var lo = 0
    var hi = this.length - 1
    while (lo <= hi) {
        var i = lo + ((hi - lo) >> 1)
        var result = compare(this[i], item)
        if (result < 0) {
            lo = i + 1
            continue
        }
        if (result > 0) {
            hi = i - 1
            continue
        }
        return i // found - return index
    }
    return ~lo // not found - return index complement
}
Array.prototype.toObject = function (propertySelector: { (item: any): string }, valueSelector: { (item: any): any }): any {
    var obj = {} as any
    for (var index = 0, count = this.length; index < count; ++index) {
        var item = this[index]
        obj[propertySelector(item)] = valueSelector(item)
    }
    return obj
}
Array.prototype.toObjectGroups = function (propertySelector: { (item: any): string }, valueSelector: { (item: any): any }): any {
    var obj = {} as any
    for (var index = 0, count = this.length; index < count; ++index) {
        var item = this[index]
        var property = propertySelector(item)
        var group = obj[property]
        if (group == undefined) {
            group = []
            obj[property] = group
        }
        group.push(valueSelector(item))
    }
    return obj
}
Array.prototype.take = function (count: number): any[] {
    return this.slice(0, Math.min(count, this.length - 1))
}
Array.prototype.clearArray = function (): any[] {
    return this.splice(0)
}

interface Math {
    /**
     * Returns the hyperbolic cosine of a number.
     * @param x A numeric expression.
     */
    cosh(x: number): number
    /**
     * Returns the hyperbolic sine of a number.
     * @param x A numeric expression.
     */
    sinh(x: number): number
    /**
     * Returns the hyperbolic tangent of a number.
     * @param x A numeric expression.
     */
    tanh(x: number): number
    /**
     * Returns the hyperbolic arc cosine of a number.
     * @param x A numeric expression.
     */
    acosh(x: number): number
    /**
     * Returns the hyperbolic arc sine of a number.
     * @param x A numeric expression.
     */
    asinh(x: number): number
    /**
     * Returns the hyperbolic arc tangent of a number.
     * @param x A numeric expression.
     */
    atanh(x: number): number
    /**
     * Returns the factorial of a number.
     * @param x A numeric expression.
     */
    fact(x: number): number
}
Math.cosh = function (x: number): number {
    return (Math.exp(x) + Math.exp(-x)) / 2
}
Math.sinh = function (x: number): number {
    return (Math.exp(x) - Math.exp(-x)) / 2
}
Math.tanh = function (x: number): number {
    return Math.sinh(x) / Math.cosh(x)
}
Math.acosh = function (x: number): number {
    return Math.log(x + Math.sqrt(x + 1)) * Math.sqrt(x - 1)
}
Math.asinh = function (x: number): number {
    return Math.log(x + Math.sqrt(1 + x * x))
}
Math.atanh = function (x: number): number {
    return 0.5 + (Math.log(1 + x) - Math.log(1 - x))
}
Math.fact = function (x: number): number {
    if (x > 170) throw 'Cannot perform a factorial on a number greater than 170.'
    var result = x
    while (--x > 1) {
        result *= x
    }
    return result
}
